import { useParams, useNavigate, Link } from 'react-router-dom';
import { jobs } from '@/data/jobs';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  MapPin,
  Briefcase,
  DollarSign,
  Calendar,
  Eye,
  Users,
  Building2,
  Globe,
  Clock,
  CheckCircle2,
  ArrowLeft,
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const job = jobs.find((j) => j.id === id);

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">职位未找到</h1>
          <Button onClick={() => navigate(ROUTE_PATHS.JOBS)}>返回职位列表</Button>
        </div>
      </div>
    );
  }

  const jobTypeMap = {
    'full-time': '全职',
    'part-time': '兼职',
    contract: '合同',
    internship: '实习',
  };

  const formatSalary = () => {
    return `${job.salary.min}K-${job.salary.max}K`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div
        className="relative h-64 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.TECH_OFFICE_1})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        <div className="relative container mx-auto px-4 h-full flex items-end pb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTE_PATHS.JOBS)}
            className="absolute top-8 left-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回职位列表
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 space-y-8"
          >
            <Card className="p-8">
              <div className="flex items-start gap-6 mb-6">
                <img
                  src={job.company.logo}
                  alt={job.company.name}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-foreground mb-2">
                    {job.title}
                  </h1>
                  <Link
                    to={`/companies/${job.company.id}`}
                    className="text-lg text-primary hover:underline"
                  >
                    {job.company.name}
                  </Link>
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {job.location}
                    </div>
                    <div className="flex items-center gap-1">
                      <Briefcase className="h-4 w-4" />
                      {jobTypeMap[job.type]}
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4" />
                      {formatSalary()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(job.postedAt)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {job.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {(job.views || job.applicants) && (
                <div className="flex gap-6 text-sm text-muted-foreground mb-6">
                  {job.views && (
                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {job.views} 次浏览
                    </div>
                  )}
                  {job.applicants && (
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {job.applicants} 人申请
                    </div>
                  )}
                </div>
              )}

              <Separator className="my-6" />

              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    职位描述
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {job.description}
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    岗位职责
                  </h2>
                  <ul className="space-y-3">
                    {job.responsibilities.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    任职要求
                  </h2>
                  <ul className="space-y-3">
                    {job.requirements.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    福利待遇
                  </h2>
                  <ul className="space-y-3">
                    {job.benefits.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="p-8">
              <h2 className="text-xl font-semibold text-foreground mb-6">
                关于公司
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <img
                    src={job.company.logo}
                    alt={job.company.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {job.company.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {job.company.industry}
                    </p>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  {job.company.description}
                </p>
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      规模：{job.company.size}人
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      地点：{job.company.location}
                    </span>
                  </div>
                  {job.company.founded && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        成立：{job.company.founded}年
                      </span>
                    </div>
                  )}
                  {job.company.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <a
                        href={job.company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        官网
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-1"
          >
            <Card className="p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                职位信息
              </h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">薪资范围</span>
                  <span className="text-sm font-semibold text-foreground">
                    {formatSalary()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">工作地点</span>
                  <span className="text-sm font-semibold text-foreground">
                    {job.location}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">工作类型</span>
                  <span className="text-sm font-semibold text-foreground">
                    {jobTypeMap[job.type]}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">发布时间</span>
                  <span className="text-sm font-semibold text-foreground">
                    {formatDate(job.postedAt)}
                  </span>
                </div>
                {job.deadline && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">截止时间</span>
                    <span className="text-sm font-semibold text-foreground">
                      {formatDate(job.deadline)}
                    </span>
                  </div>
                )}
              </div>

              <Separator className="my-6" />

              <Button className="w-full" size="lg">
                立即申请
              </Button>

              <Button variant="outline" className="w-full mt-3" size="lg">
                收藏职位
              </Button>

              {job.deadline && (
                <div className="mt-6 p-4 bg-accent/10 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-accent">
                    <Clock className="h-4 w-4" />
                    <span>距离截止还有 {Math.ceil((new Date(job.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} 天</span>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}